/**
 * Add to Wishlist Action
 * Triggered by: arc:action:wishlistAdd
 */

import shopifyHelper from "../../js/shopify";

// Global wishlist state
let wishlistProducts = JSON.parse(localStorage.getItem('wishlist_products') || '[]');

export const runWishlistAdd = async (data = {}) => {
    try {
        const { 
            productId,
            variantId,
            product,
            showNotification = true,
            preventDuplicates = true,
            fetchProductData = true
        } = data;

        if (!productId && !variantId && !product) {
            throw new Error('Product ID, variant ID, or product data is required');
        }

        let finalProductId = productId;
        
        // If variant ID is provided, extract product ID
        if (!finalProductId && variantId) {
            // For now, assume variant ID can be used to find product
            finalProductId = variantId;
        }

        // Check for duplicates if prevention is enabled
        if (preventDuplicates) {
            const isDuplicate = wishlistProducts.some(product => 
                parseInt(product.id) === parseInt(finalProductId)
            );
            
            if (isDuplicate) {
                window.dispatchEvent(new CustomEvent('wishlist:alreadyExists', {
                    detail: {
                        productId: finalProductId,
                        message: 'Product is already in your wishlist'
                    }
                }));

                return {
                    success: false,
                    alreadyExists: true,
                    productId: finalProductId,
                    message: 'Product is already in your wishlist'
                };
            }
        }

        // Dispatch add attempt event
        window.dispatchEvent(new CustomEvent('wishlist:addAttempt', {
            detail: {
                productId: finalProductId,
                variantId,
                showNotification
            }
        }));

        let productToAdd = product;

        // Fetch product data if not provided or if fetchProductData is true
        if (!productToAdd || fetchProductData) {
            console.log('Adding to wishlist:', finalProductId);
            const transformedProducts = await shopifyHelper.getShopifyProductList([finalProductId]);
            
            if (!transformedProducts || transformedProducts.length === 0) {
                throw new Error('Product not found');
            }
            
            productToAdd = transformedProducts[0];
            console.log('Product data retrieved:', productToAdd);
        }

        // Add to wishlist
        wishlistProducts = wishlistProducts.concat([productToAdd]);
        
        // Save to localStorage
        localStorage.setItem('wishlist_products', JSON.stringify(wishlistProducts));

        // Dispatch success event
        window.dispatchEvent(new CustomEvent('wishlist:addSuccess', {
            detail: {
                product: productToAdd,
                productId: finalProductId,
                totalItems: wishlistProducts.length,
                wishlist: wishlistProducts,
                timestamp: Date.now()
            }
        }));

        // Dispatch wishlist updated event
        window.dispatchEvent(new CustomEvent('wishlist:updated', {
            detail: {
                action: 'add',
                product: productToAdd,
                wishlist: wishlistProducts,
                totalItems: wishlistProducts.length
            }
        }));

        if (showNotification) {
            window.dispatchEvent(new CustomEvent('notification:show', {
                detail: {
                    type: 'success',
                    message: 'Added to wishlist',
                    product: productToAdd
                }
            }));
        }

        return {
            success: true,
            product: productToAdd,
            productId: finalProductId,
            totalItems: wishlistProducts.length,
            wishlist: wishlistProducts,
            message: 'Product added to wishlist successfully'
        };

    } catch (error) {
        console.error('Wishlist Add error:', error);
        
        window.dispatchEvent(new CustomEvent('wishlist:addError', {
            detail: {
                error: error.message,
                productId: data.productId || data.variantId
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

// Get current wishlist
export const getWishlist = () => {
    return wishlistProducts;
};

// Get wishlist count
export const getWishlistCount = () => {
    return wishlistProducts.length;
};

// Check if item is in wishlist
export const isItemInWishlist = (productId) => {
    return wishlistProducts.some(product => 
        parseInt(product.id) === parseInt(productId)
    );
};

// Initialize wishlist system
export const initializeWishlist = (config = {}) => {
    const {
        buttonSelector = '[data-wishlist-add]',
        autoSetupButtons = true,
        persistToLocalStorage = true
    } = config;

    // Load wishlist from localStorage
    if (persistToLocalStorage) {
        const stored = localStorage.getItem('wishlist_products');
        if (stored) {
            try {
                wishlistProducts = JSON.parse(stored);
            } catch (e) {
                console.error('Error parsing stored wishlist:', e);
                wishlistProducts = [];
            }
        }
    }

    if (autoSetupButtons) {
        setupWishlistButtons(buttonSelector);
    }

    // Set up global wishlist access
    window.wishlist = {
        getWishlist,
        getWishlistCount,
        isItemInWishlist,
        products: wishlistProducts
    };

    // Dispatch initialization event
    window.dispatchEvent(new CustomEvent('wishlist:initialized', {
        detail: {
            config,
            totalItems: wishlistProducts.length,
            timestamp: Date.now()
        }
    }));
};

// Setup wishlist button listeners
const setupWishlistButtons = (buttonSelector) => {
    const wishlistButtons = document.querySelectorAll(buttonSelector);
    
    wishlistButtons.forEach(button => {
        button.removeEventListener('click', handleWishlistAddClick);
        button.addEventListener('click', handleWishlistAddClick);
        
        // Update button state based on current wishlist
        updateButtonState(button);
    });
};

// Handle wishlist add button clicks
const handleWishlistAddClick = async (event) => {
    event.preventDefault();
    
    const button = event.target.closest('[data-wishlist-add]');
    if (!button) return;
    
    const productId = button.dataset.productId || button.dataset.wishlistAdd;
    const variantId = button.dataset.variantId;
    const showNotification = button.dataset.showNotification !== 'false';

    if (!productId && !variantId) {
        console.error('Product ID or variant ID not found for wishlist button');
        return;
    }

    // Check if already in wishlist
    if (isItemInWishlist(productId || variantId)) {
        // Trigger remove instead if already in wishlist
        window.dispatchEvent(new CustomEvent('arc:action:wishlistRemove', {
            detail: {
                productId: productId || variantId,
                showNotification
            }
        }));
        return;
    }

    // Update button state
    button.classList.add('adding');
    const originalText = button.textContent;
    const loadingText = button.dataset.loadingText || 'Adding...';
    button.textContent = loadingText;

    try {
        const result = await window.dispatchEvent(new CustomEvent('arc:action:wishlistAdd', {
            detail: {
                productId,
                variantId,
                showNotification
            }
        }));

        // Update button state on success
        if (result?.success !== false) {
            updateButtonState(button, true);
        }

    } finally {
        // Reset button state
        button.classList.remove('adding');
        if (!isItemInWishlist(productId || variantId)) {
            button.textContent = originalText;
        }
    }
};

// Update button state based on wishlist status
const updateButtonState = (button, isInWishlist = null) => {
    const productId = button.dataset.productId || button.dataset.wishlistAdd;
    const variantId = button.dataset.variantId;
    const checkId = productId || variantId;
    
    const inWishlist = isInWishlist !== null ? isInWishlist : isItemInWishlist(checkId);
    
    if (inWishlist) {
        button.classList.add('in-wishlist');
        button.setAttribute('aria-pressed', 'true');
        if (button.dataset.addedText) {
            button.textContent = button.dataset.addedText;
        }
    } else {
        button.classList.remove('in-wishlist');
        button.setAttribute('aria-pressed', 'false');
        if (button.dataset.defaultText) {
            button.textContent = button.dataset.defaultText;
        }
    }
};

// Update all wishlist buttons when wishlist changes
export const updateAllWishlistButtons = () => {
    const buttons = document.querySelectorAll('[data-wishlist-add]');
    buttons.forEach(updateButtonState);
};

// Listen for wishlist updates to refresh button states
window.addEventListener('wishlist:updated', () => {
    updateAllWishlistButtons();
});

export default runWishlistAdd;
